import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useApi } from "@/lib/api";
import { useUserMe } from "@/lib/useUserMe";
import { Section } from "@/components/intel/Section";
import { ItemCardEmpty } from "@/components/intel/ItemCard";
import {
  OpportunityCard,
  type Opportunity,
} from "@/components/opps/OpportunityCard";
import { RecapPanel } from "@/components/opps/RecapPanel";

type OppsPayload = { opportunities: Opportunity[] };

export function Opportunities() {
  const api = useApi();
  const me = useUserMe();
  const region = me.data?.user?.region ?? null;

  // Recommended: ranked by user follows + geo match. May be empty if neither set.
  const recommended = useQuery({
    queryKey: ["opportunities", "recommended", region],
    queryFn: () =>
      api.get<OppsPayload>(
        `/opportunities?lens=recommended&limit=6${region ? `&geo=${encodeURIComponent(region)}` : ""}`,
      ),
  });

  const active = useQuery({
    queryKey: ["opportunities", "active"],
    queryFn: () => api.get<OppsPayload>("/opportunities?lens=active&limit=20"),
  });

  const expiring = useQuery({
    queryKey: ["opportunities", "expiring"],
    queryFn: () => api.get<OppsPayload>("/opportunities?lens=expiring&limit=10"),
  });

  return (
    <div className="px-10 py-10 max-w-5xl">
      <header className="mb-12">
        <p className="label-eyebrow">Engine</p>
        <h1 className="mt-2 text-display-2 font-display">Opportunities</h1>
        <p className="mt-3 text-whisper max-w-xl leading-relaxed">
          Curated grants, fellowships, accelerators, partnerships, jobs, and funding
          surfaced by the synthesis layer. Quiet by default — hover any card to act on it.
        </p>
      </header>

      <RecapPanel />

      {region && recommended.data && recommended.data.opportunities.length > 0 && (
        <Section
          eyebrow="Recommended"
          title="Matched to your focus"
          detail={`Filtered by your followed topics${region ? ` and ${region}` : ""}.`}
        >
          <OppList q={recommended} empty="No matches yet." />
        </Section>
      )}

      <Section
        eyebrow="Active"
        title="Open opportunities"
        detail="Quality-gated by extraction confidence and urgency."
      >
        <OppList q={active} empty="No active opportunities yet." />
      </Section>

      {expiring.data && expiring.data.opportunities.length > 0 && (
        <Section
          eyebrow="Closing soon"
          title="Deadlines approaching"
          detail="Sorted by nearest deadline first."
        >
          <OppList q={expiring} empty="Nothing closing soon." />
        </Section>
      )}
    </div>
  );
}

function OppList({
  q,
  empty,
}: {
  q: UseQueryResult<OppsPayload>;
  empty: string;
}) {
  if (q.isLoading) return <ItemCardEmpty label="Reading opportunities…" />;
  if (q.isError) return <ItemCardEmpty label="Couldn't load this section." />;
  const items = q.data?.opportunities ?? [];
  if (items.length === 0) {
    return (
      <ItemCardEmpty
        label={empty}
        detail="Opportunities are extracted by the synthesis worker as signals arrive."
      />
    );
  }
  return (
    <div className="grid gap-4">
      {items.map((o) => (
        <OpportunityCard key={o.id} opp={o} variant="full" />
      ))}
    </div>
  );
}
